import { Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import Layout from './components/Layout/Layout'
import Login from './pages/Login/Login'
import Dashboard from './pages/Dashboard/Dashboard'
import Agents from './pages/Agents/Agents'
import Chat from './pages/Chat/Chat'
import Tasks from './pages/Tasks/Tasks'
import Settings from './pages/Settings/Settings'
import Opportunities from './pages/Opportunities/Opportunities'
import Leads from './pages/Leads/Leads'
import Clients from './pages/Clients/Clients'
import { AuthProvider, useAuth } from './context/AuthContext'
import { SocketProvider } from './context/SocketContext'
import { toast } from 'react-hot-toast'
import './styles/global.css'

// API base URL
const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:3001/api'

// Health check component
function HealthCheck() {
  const { data: health, isLoading, error } = useQuery('health', async () => {
    const response = await fetch(`${API_BASE}/health`)
    if (!response.ok) throw new Error('Health check failed')
    return response.json()
  }, {
    refetchInterval: 30000, // Check every 30 seconds
  })

  useEffect(() => {
    if (error) {
      toast.error('Backend connection failed. Please check if the server is running.')
    }
  }, [error])

  if (isLoading) return null

  return (
    <div style={{ display: 'none' }}>
      Backend: {health?.status || 'unknown'}
    </div>
  )
}

// Protected route component
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <div className="loading-text">Authenticating...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return children
}

// Main App component
function AppContent() {
  const { isAuthenticated } = useAuth()

  return (
    <>
      <HealthCheck />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={
          <ProtectedRoute>
            <SocketProvider>
              <Layout />
            </SocketProvider>
          </ProtectedRoute>
        }>
          <Route index element={<Dashboard />} />
          <Route path="agents" element={<Agents />} />
          <Route path="chat" element={<Chat />} />
          <Route path="chat/:agentId" element={<Chat />} />
          <Route path="opportunities" element={<Opportunities />} />
          <Route path="clients" element={<Clients />} />
          <Route path="leads" element={<Leads />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

// Root App component
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App